/**
 * RunSummaryCard component
 * Displays a summary card for an agentic run
 */
import { Link } from 'react-router-dom'
import { GitBranch, ExternalLink, Clock, Folder } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { RunStatus } from '@/lib/types'

export interface RunSummary {
  /** Run ID */
  runId: string
  /** Current status */
  status: RunStatus
  /** Repository URL */
  repoUrl: string
  /** Repository name */
  repoName: string
  /** Goal/task description */
  goal: string
  /** Pull request URL */
  prUrl?: string
  /** When the run was created */
  createdAt: string
}

export interface RunSummaryCardProps {
  /** Run data */
  run: RunSummary
}

const statusColors: Record<string, string> = {
  SUCCEEDED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  RUNNING: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  PENDING: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  QUEUED: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  TIMEOUT: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
}

export function RunSummaryCard({ run }: RunSummaryCardProps) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Truncate run ID for display
  const displayRunId = run.runId.length > 30 
    ? run.runId.slice(0, 27) + '...' 
    : run.runId

  return (
    <article
      role="article"
      data-status={run.status}
      className="rounded-lg border bg-card text-card-foreground p-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <Link
          to={`/runs/${run.runId}`}
          className="font-mono text-sm font-medium hover:underline truncate"
          title={run.runId}
        >
          {displayRunId}
        </Link>
        <span
          className={cn(
            'px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
            statusColors[run.status] || statusColors.PENDING
          )}
        >
          {run.status}
        </span>
      </div>

      {/* Goal */}
      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
        {run.goal}
      </p>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
        {/* Repository */}
        <a
          href={run.repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 hover:text-foreground"
        >
          <Folder className="h-3.5 w-3.5" />
          <span>{run.repoName}</span>
          <ExternalLink className="h-3 w-3" />
        </a>

        {/* PR Link */}
        {run.prUrl && (
          <a
            href={run.prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline"
          >
            <GitBranch className="h-3.5 w-3.5" />
            <span>View PR</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {/* Timestamp */}
      <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" />
        <span>{formatDate(run.createdAt)}</span>
      </div>
    </article>
  )
}
